import { REGISTRY_BLOCK } from '../catalog/platformCatalogIds';

/**
 * Lightbox finish for Image Gallery. Arrangement stays on `registryBlockKey`.
 * A stored `content.galleryLightbox` override wins over the arrangement pairing.
 */
export const GALLERY_LIGHTBOX = {
  /** Full-viewport photo, boxed controls, caption pill. */
  stage: 'stage',
  /** Raised sheet: title, round arrows, thumbnail rail. */
  catalog: 'catalog',
  /** Edge-to-edge photograph, index, unboxed chevrons. */
  cinema: 'cinema',
} as const;

export const GALLERY_LIGHTBOX_VALUES = [
  GALLERY_LIGHTBOX.stage,
  GALLERY_LIGHTBOX.catalog,
  GALLERY_LIGHTBOX.cinema,
] as const;

export type GalleryLightbox = (typeof GALLERY_LIGHTBOX_VALUES)[number];

export function isGalleryLightbox(value: string): value is GalleryLightbox {
  return (GALLERY_LIGHTBOX_VALUES as readonly string[]).includes(value);
}

const P = REGISTRY_BLOCK.presentation;

/** Arrangement → lightbox Look. Keep in sync with `specs/components/gallery-variants.md`. */
export const GALLERY_HARMONIZED_LIGHTBOX = {
  [P.imageGalleryMosaic]: GALLERY_LIGHTBOX.stage,
  [P.imageGalleryMasonry]: GALLERY_LIGHTBOX.cinema,
  [P.imageGalleryFilmstrip]: GALLERY_LIGHTBOX.catalog,
  [P.imageGallerySpotlight]: GALLERY_LIGHTBOX.catalog,
  [P.imageGalleryLedger]: GALLERY_LIGHTBOX.cinema,
} as const satisfies Record<string, GalleryLightbox>;

export function harmonizedGalleryLightbox(
  registryBlockKey: string | null | undefined,
): GalleryLightbox {
  if (!registryBlockKey) {
    return GALLERY_LIGHTBOX.stage;
  }
  const paired = (GALLERY_HARMONIZED_LIGHTBOX as Record<string, GalleryLightbox>)[registryBlockKey];
  return paired ?? GALLERY_LIGHTBOX.stage;
}

/** Reads optional `galleryLightbox` from block content (same value across locales). */
export function readGalleryLightboxOverride(
  content: Record<string, Record<string, string>>,
): string | undefined {
  const field = content.galleryLightbox;
  if (!field) {
    return undefined;
  }
  const raw = field.de ?? field.en ?? field.vi ?? Object.values(field)[0];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

/**
 * Priority: stored `galleryLightbox` → arrangement pairing → `stage`.
 */
export function resolveGalleryLightbox(
  registryBlockKey: string | null | undefined,
  content: Record<string, Record<string, string>> | undefined,
): GalleryLightbox {
  const raw = content ? readGalleryLightboxOverride(content) : undefined;
  if (raw && isGalleryLightbox(raw)) {
    return raw;
  }
  return harmonizedGalleryLightbox(registryBlockKey);
}

/** Modifier class for non-default looks. Stage uses the base `.ws-gallery__lightbox` styles. */
export function galleryLightboxClass(look: GalleryLightbox): string {
  return look === GALLERY_LIGHTBOX.stage ? '' : `ws-gallery__lightbox--${look}`;
}
